import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import { buildPaymentDetailsForSubmit } from "@/lib/onlinePayments";
import { getStartTimeOfServiceInLinked } from "@/pages/Dashboard/Calendar/utils";
import { getPriceCents } from "@/utils/price-utils";
import { getCleanAccessToken } from "@/utils/auth-token";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import { isValidPhoneNumber } from "react-phone-number-input";
import { create } from "zustand";
import {
  fullPhoneNumberToNumberAndCountryCode,
  getDurationsAndPrice,
} from "../../Utilities";
import {
  apiCreateBreak,
  apiUpdateCustomerOnPreScheduledAppointment,
} from "../../pages/Dashboard/Calendar/api/apiCalendar";
import { useCalendarStore } from "../../pages/Dashboard/Calendar/store/useCalendarStore";
import { apiGetAppointment, apiUpdateAppointment } from "./apiAppointment";
import {
  getRecordedRequestedUserIds,
  isRecordedRequestOnline,
} from "./components/requestedStaffChange";
import { addToast } from "@heroui/react";

const buildContactPersonPayload = ({ values, attendeeCustomerId }) => {
  const mode = values.contactPersonMode || "none";

  if (mode === "none") {
    return null;
  }

  if (mode === "existing") {
    const contactCustomerId =
      values.contactCustomerId != null
        ? Number(values.contactCustomerId)
        : undefined;

    if (!contactCustomerId || contactCustomerId === attendeeCustomerId) {
      return null;
    }

    return {
      customerId: contactCustomerId,
    };
  }

  if (mode === "new") {
    const name = values.contactName?.trim();
    const lastName = values.contactLastName?.trim();

    if (!name || !lastName) {
      return null;
    }

    let gsm = values.contactGsm || undefined;
    let countryCode =
      values.contactCountryCode || values.countryCode || undefined;

    if (gsm && isValidPhoneNumber(gsm)) {
      const parsedPhone = fullPhoneNumberToNumberAndCountryCode(gsm);
      gsm = parsedPhone.gsm;
      countryCode = parsedPhone.countryCode;
    }

    return {
      name,
      lastName,
      email: values.contactEmail?.trim() || undefined,
      gsm,
      countryCode,
      language: values.language,
    };
  }

  return null;
};

export const useAppointmentModalStore = create((set, get) => ({
  resetData: () => {
    set({
      isFetchingData: false,
      appointmentData: null,
      selectedServices: [],
      confirmedServiceSelection: false,
      selectedUserAppointment: null,

      invalidAppointmentsData: null,
      rescheduleAppointmentData: null,
      rescheduleSendNotifications: true,
      rescheduleAllLinked: true,
      canDismissDrawer: true,
      isCustomOnlinePaymentAmount: false,
      notificationSettings: [],
      notificationSettingsInitialized: false,
      hiddenDisabledNotificationSettingIds: [],
    });
  },

  // Lives here rather than in the appointment form so the reschedule
  // confirmation modal — rendered outside the form — can toggle the same list.
  notificationSettings: [],
  notificationSettingsInitialized: false,
  setNotificationSettings: (notificationSettings) =>
    set({ notificationSettings, notificationSettingsInitialized: true }),
  setNotificationSettingEnabled: (settingId, enabled) =>
    set((state) => ({
      notificationSettings: state.notificationSettings.map((setting) =>
        setting.id === settingId ? { ...setting, enabled } : setting,
      ),
    })),

  // Settings the form can't render a switch for (variant doesn't cover the
  // selected service/location, or their send time already passed). The API
  // replaces the whole disabled list on save, so they have to ride along or
  // their off state is lost.
  hiddenDisabledNotificationSettingIds: [],
  setHiddenDisabledNotificationSettingIds: (
    hiddenDisabledNotificationSettingIds,
  ) => set({ hiddenDisabledNotificationSettingIds }),

  getNotificationSettingsForApi: () => {
    // A save that lands before the settings query resolves would otherwise
    // submit an empty list, and the API reads that as "nothing is disabled".
    if (!get().notificationSettingsInitialized) {
      const storedDisabledIds = (
        get().appointmentData?.customers ?? []
      ).flatMap((customer) => customer.disabledNotificationSettingIds ?? []);

      return [...new Set(storedDisabledIds)].map((id) => ({
        id,
        enabled: false,
      }));
    }

    return [
      ...get().notificationSettings,
      ...get().hiddenDisabledNotificationSettingIds.map((id) => ({
        id,
        enabled: false,
      })),
    ];
  },

  canDismissDrawer: true,
  setCanDismissDrawer: (canDismissDrawer) => set({ canDismissDrawer }),

  isCustomOnlinePaymentAmount: false,
  setIsCustomOnlinePaymentAmount: (isCustomOnlinePaymentAmount) =>
    set({ isCustomOnlinePaymentAmount }),

  currentAppointmentGsm: null,
  setCurrentAppointmentGsm: (currentAppointmentGsm) =>
    set({ currentAppointmentGsm }),

  currentAppointmentEmail: null,
  setCurrentAppointmentEmail: (currentAppointmentEmail) =>
    set({ currentAppointmentEmail }),

  requiredTags: [],
  setRequiredTags: (requiredTags) =>
    set((state) => ({
      requiredTags:
        typeof requiredTags === "function"
          ? requiredTags(state.requiredTags)
          : requiredTags,
    })),

  // --- MANTINE MODAL TO HERO MODAL DATA

  invalidAppointmentsData: null,
  setInvalidAppointmentsData: (invalidAppointmentsData) =>
    set({ invalidAppointmentsData }),

  rescheduleAppointmentData: null,
  setRescheduleAppointmentData: (rescheduleAppointmentData) =>
    set({ rescheduleAppointmentData }),

  // ---

  lastCreatedAppointmentId: null,

  appointmentId: null,
  setAppointmentId: (appointmentId) => {
    set({ appointmentId });
    if (appointmentId === -1) {
      set({ lastCreatedAppointmentId: null });
      get().initializeNewAppointmentData();
    } else {
      get().fetchAppointmentData(appointmentId);
    }
  },

  initializeNewAppointmentData: () => {
    const calendarStoreState = useCalendarStore.getState();
    const newAppointmentData = {
      appointmentId: null,
      customers: [],
      duration: 30,
      locationId: calendarStoreState.selectedLocationId,
      serviceColor: "#000000",
      serviceId: null,
      serviceName: "",
      startTime:
        calendarStoreState.lastSelectedTime ??
        dayjs().format("YYYY-MM-DDTHH:mm"),
      userId: calendarStoreState.lastSelectedUserId ?? null,
      resources: calendarStoreState.lastSelectedResourceId
        ? [calendarStoreState.lastSelectedResourceId]
        : [],
      invoiceId: null,
    };

    set({
      appointmentData: newAppointmentData,
      initialAppointmentData: newAppointmentData,
      selectedServices: [],
      confirmedServiceSelection: false,
      selectedUserAppointment: null,
      isFetchingData: false,
      isCustomOnlinePaymentAmount: false,
    });
  },

  isFetchingData: false,
  setIsFetchingData: (isFetchingData) => set({ isFetchingData }),

  appointmentData: null,
  initialAppointmentData: null,
  setInitialAppointmentData: (appointmentData) =>
    set({ initialAppointmentData: appointmentData }),
  setAppointmentData: (appointmentData) => set({ appointmentData }),
  modifyAppointmentData: (appointmentData) =>
    set((state) => ({
      appointmentData: {
        ...state.appointmentData,
        ...appointmentData,
      },
    })),

  selectedServices: [],
  setSelectedServices: (
    selectedServices,
    overridePrice = true,
    overrideDuration = true,
  ) => {
    const { services, lastSelectedUserId } = useCalendarStore.getState();

    if (!get().appointmentData) return;

    const appointmentStart = dayjs(get().appointmentData.startTime);
    const areServicesLinked = (previousService, currentService) => {
      if (!previousService || !currentService) {
        return false;
      }

      const currentUsers = currentService.users || [];
      const currentResources = currentService.resources || [];

      const previousUsers = previousService.users || [];
      const previousResources = previousService.resources || [];

      return (
        currentUsers.some((user) => previousUsers.includes(user)) ||
        currentResources.some((resource) =>
          previousResources.includes(resource),
        )
      );
    };

    const servicesWithPrices = selectedServices.map((service, index) => {
      const serviceStart = getStartTimeOfServiceInLinked({
        selectedServices,
        index,
        startTime: appointmentStart,
      });

      const currService = services.find(
        (s) => s.serviceId === service.serviceId,
      );

      const userId = service.users[0] ?? lastSelectedUserId;

      const user = currService.users.find((u) => u.userId === userId);

      const {
        startDuration,
        timeOffDuration,
        endDuration,
        price: userPrice,
      } = getDurationsAndPrice(currService, user);

      const baseDuration =
        startDuration || 0 + timeOffDuration || 0 + endDuration || 0;

      // A saved part keeps its booked length; defaults only seed parts not yet saved.
      const shouldOverrideDuration = overrideDuration && !service.appointmentId;

      const duration = shouldOverrideDuration
        ? startDuration || 0 + timeOffDuration || 0 + endDuration || 0
        : service.startDuration ||
          0 + service.endDuration ||
          0 + service.timeOffDuration ||
          0;

      const priceBase =
        (userPrice ? userPrice * 100 : null) || currService.priceBaseCents;

      const priceCents = getPriceCents({
        service: currService,
        serviceStart,
        duration,
        baseDuration,
        resourceCount: service.resources.length,
        attendance: service.attendance,
        priceBase,
      });

      const shouldApplyBlocksBetweenLinkedAppointments =
        currService.applyBlocksBetweenLinkedAppointments ?? true;

      const previousService = selectedServices
        .slice(0, index)
        .reverse()
        .find((selectedService) => !selectedService?.removed);
      const nextService = selectedServices
        .slice(index + 1)
        .find((selectedService) => !selectedService?.removed);

      let blockBeforeMins = service.blockBeforeMins;
      let blockAfterMins = service.blockAfterMins;

      if (!shouldApplyBlocksBetweenLinkedAppointments) {
        if (areServicesLinked(previousService, service)) {
          blockBeforeMins = 0;
        }

        if (areServicesLinked(service, nextService)) {
          blockAfterMins = 0;
        }
      }

      return {
        ...service,
        price: overridePrice ? priceCents / 100 : service.price,
        startDuration: shouldOverrideDuration
          ? startDuration
          : service.startDuration,
        timeOffDuration: shouldOverrideDuration
          ? timeOffDuration
          : service.timeOffDuration,
        endDuration: shouldOverrideDuration ? endDuration : service.endDuration,
        blockBeforeMins,
        blockAfterMins,
      };
    });

    set({ selectedServices: servicesWithPrices });
  },

  confirmedServiceSelection: false,
  setConfirmedServiceSelection: (confirmedServiceSelection) =>
    set({ confirmedServiceSelection }),
  handleConfirmServiceSelection: (confirmed, _services) => {
    const calendarStoreState = useCalendarStore.getState();
    const { tagEnforcedOrders } = useCalendarStore.getState();

    const newSelectedServices = _services ?? [...get().selectedServices];

    const serviceTags = new Map();

    newSelectedServices.forEach((ser) => {
      const service = calendarStoreState.services.find(
        (service) => service.serviceId === ser.serviceId,
      );

      serviceTags.set(service.serviceId, service.tagId);

      ser.userId = calendarStoreState.lastSelectedUserId;
      ser.users = calendarStoreState.lastSelectedUserId
        ? [calendarStoreState.lastSelectedUserId]
        : [];

      if (service) {
        const user = service.users.find((user) => user.userId === ser.users[0]);

        const { startDuration, timeOffDuration, endDuration, price } =
          getDurationsAndPrice(service, user);

        ser.price = price;
        ser.startDuration = startDuration;
        ser.timeOffDuration = timeOffDuration;
        ser.endDuration = endDuration;
        ser.blockBeforeMins = service.blockBeforeMins;
        ser.blockAfterMins = service.blockAfterMins;
        ser.formPaymentRequired = service.formPaymentRequired;
        ser.discountType = "amount";
      }

      //  ser.resources = calendarStoreState.lastSelectedResourceId
      //   ? [calendarStoreState.lastSelectedResourceId]
      //  : [];
    });

    if (tagEnforcedOrders.length) {
      const tags = newSelectedServices.map((service) =>
        serviceTags.get(service.serviceId),
      );

      //find tagEnforcedOrders that have all tags in tags and is same lenght
      const selectedCombination = tagEnforcedOrders.find(
        (tagEnforcedOrder) =>
          tagEnforcedOrder.every((tag) => tags.includes(tag)) &&
          tagEnforcedOrder.length === tags.length,
      );

      if (selectedCombination) {
        // sort newSelectedServices by selectedCombination array index

        newSelectedServices.sort(
          (a, b) =>
            selectedCombination.indexOf(serviceTags.get(a.serviceId)) -
            selectedCombination.indexOf(serviceTags.get(b.serviceId)),
        );
      }
    }

    get().setSelectedServices(newSelectedServices);

    get().setConfirmedServiceSelection(confirmed);

    if (confirmed) {
      calendarStoreState.setHideModalTabs(true);
    }
  },

  selectedUserAppointment: null,
  setSelectedUserAppointment: (selectedUserAppointment) =>
    set({ selectedUserAppointment }),

  rescheduleSendNotifications: true,
  setRescheduleSendNotifications: (rescheduleSendNotifications) =>
    set({ rescheduleSendNotifications }),
  rescheduleAllLinked: true,
  setRescheduleAllLinked: (rescheduleAllLinked) => set({ rescheduleAllLinked }),
  wirelesslySubmitAppointmentForm: () => {
    const dateTimeChanged =
      get().appointmentData?.startTime !==
      get().initialAppointmentData?.startTime;

    set({ rescheduleSendNotifications: true });
    set({ rescheduleAllLinked: true });

    if (
      useCalendarStore.getState().selectedAppointmentId !== -1 &&
      dateTimeChanged
    ) {
      get().setCanDismissDrawer(false);

      get().setRescheduleAppointmentData({
        appointmentData: get().appointmentData,
      });
    } else {
      document.getElementById("appointment-form-submit").click();
    }
  },
  // appointment-form-submit
  isCreatingAppointment: false,
  handleSubmitAppointmentForm: async (values) => {
    if (get().isCreatingAppointment) {
      return;
    }

    set({ isCreatingAppointment: true });

    if (get().appointmentData?.isPreScheduled) {
      const isAddingCustomer = get().selectedUserAppointment == null;

      let response;
      let notificationText;

      const data = { ...values };

      if (data.gsm && data.gsm !== "" && isValidPhoneNumber(data.gsm)) {
        const { gsm, countryCode } = fullPhoneNumberToNumberAndCountryCode(
          data.gsm,
        );

        data.countryCode = countryCode;
        data.gsm = gsm;
      }

      data.appointmentIds = [
        get().appointmentData.appointmentParts[0].appointmentId,
        ...(values.additionalPrescheduledAppointmentIds ?? []),
      ];

      data.customerSubscriptionId = values.customerSubscriptionId ?? undefined;

      data.customer = {
        name: data.name,
        lastName: data.lastName,
        email: data.email?.length > 0 ? data.email : undefined,
        gsm: data.gsm,
        countryCode: data.countryCode,
        customerId: data.customerId ?? undefined,
        language: data.language,
      };

      const contactPersonPayload = buildContactPersonPayload({
        values,
        attendeeCustomerId: data.customerId ?? undefined,
      });
      data.contactPerson = contactPersonPayload;
      data.notificationSettings = get().getNotificationSettingsForApi();

      if (isAddingCustomer) {
        try {
          response = await axios.post(
            `${import.meta.env.VITE_BE_HOST}/appointment/prescheduled/customer`,
            data,
            {
              headers: {
                "x-access-token": getCleanAccessToken(),
              },
            },
          );
        } catch (e) {
          console.error(e);
          addToast({
            title: e.response?.data?.error || e.message,
            color: "danger",
          });
        }

        notificationText = <Trans>Uspešno ste dodali stranko na termin</Trans>;
      } else {
        response = await apiUpdateCustomerOnPreScheduledAppointment(
          get().appointmentData.appointmentParts[0].appointmentId,
          get().selectedUserAppointment,
          {
            ...values,
            notificationSettings: get().getNotificationSettingsForApi(),
            contactPerson: contactPersonPayload,
          },
        ).catch((err) => {
          console.error(err);
          addToast({
            title: err.response?.data?.error || err.message,
            color: "danger",
          });
        });

        notificationText = (
          <Trans>Uspešno ste posodobili stranko na terminu</Trans>
        );
      }

      if (!response || response.status !== 200) {
        if (response) {
          console.error("Napaka");

          addToast({
            title: <Trans>Napaka</Trans>,
            color: "danger",
          });
        }

        set({ isCreatingAppointment: false });

        return;
      }

      addToast({
        title: notificationText,
        color: "success",
      });

      get().setSelectedUserAppointment(null);
      // refresh the appointment
      get().fetchAppointmentData(get().appointmentId);

      set({ isCreatingAppointment: false });

      return;
    }

    const isNewAppointment = get().appointmentId === -1;

    const customerGsm = values.gsm;
    if (customerGsm && isValidPhoneNumber(customerGsm)) {
      const { gsm, countryCode } =
        fullPhoneNumberToNumberAndCountryCode(customerGsm);

      values.gsm = gsm;
      values.countryCode = countryCode;
    }

    const dataForApi = {
      customerData: {
        ...values,
        customerSubscriptionId: values.customerSubscriptionId,
        tagIds: values.selectedTagIds,
        defaultTagIds: values.selectedDefaultIds,
        // birthday: values.birthday
        //   ? dayjs(values.birthday).format("YYYY-MM-DD")
        //   : null,
      },
      notificationSettings: get().getNotificationSettingsForApi(),
      appointments: [],
      repeatData: {
        isRepeating: values.isRepeating,
        repeatInterval: values.repeatInterval,
        repeatDuration: values.repeatDuration,
        repeatType: values.repeatType,
      },
      payOnlyFirstAppointment: values.payOnlyFirstAppointment,
      discountCodeId: values.discountCode ?? undefined,
    };

    delete dataForApi.customerData.isRepeating;
    delete dataForApi.customerData.repeatDuration;
    delete dataForApi.customerData.repeatType;

    get().selectedServices.forEach((selectedService) => {
      const service = useCalendarStore
        .getState()
        .services.find(
          (service) => service.serviceId === selectedService.serviceId,
        );

      let duration = selectedService.startDuration;
      if (duration == null) {
        duration = selectedService.endDuration;
      }

      dataForApi.appointments.push({
        serviceId: service.serviceId,
        duration: selectedService.timeOffDuration
          ? selectedService.startDuration +
            selectedService.endDuration +
            selectedService.timeOffDuration
          : duration,
        locationId: get().appointmentData.locationId,
        userId: selectedService.userId,
        users: selectedService.users || [],
        price: selectedService.price,
        timeOffStart: selectedService.startDuration,
        timeOffDuration: selectedService.timeOffDuration,
        resources: selectedService.resources || [],
        appointmentId: selectedService.appointmentId,
        removed: selectedService.removed,
        blockBeforeMins: selectedService.blockBeforeMins,
        blockAfterMins: selectedService.blockAfterMins,
        attendance: selectedService.attendance,
        discountType: selectedService.discountType,
        discountAmount:
          selectedService.discountType == "amount"
            ? Math.round(selectedService.discountAmount * 100)
            : selectedService.discountAmount,
        // Sent only when touched, so saving never overwrites how a form
        // booking recorded the customer's choice.
        customerRequestedStaff:
          selectedService.isCustomerRequestedStaffEdited ||
          !selectedService.appointmentId
            ? (selectedService.customerRequestedStaff ?? false)
            : undefined,
      });
    });

    if (
      dataForApi.gsm &&
      dataForApi.gsm !== "" &&
      isValidPhoneNumber(dataForApi.gsm)
    ) {
      const { gsm, countryCode } = fullPhoneNumberToNumberAndCountryCode(
        values.gsm,
      );
      dataForApi.countryCode = countryCode;
      dataForApi.gsm = gsm;
    }

    const dayObject = dayjs(get().appointmentData.startTime);

    // dataForApi.services = selectedServices;
    // dataForApi.startTime = appointmentData.startTime;
    // dataForApi.duration = appointmentData.duration;
    dataForApi.locationId = get().appointmentData.locationId;
    dataForApi.userId = get().appointmentData.userId;
    dataForApi.time = dayObject.format("HH:mm");
    dataForApi.date = dayObject.format("YYYY-MM-DD");
    dataForApi.sendNotifications = get().rescheduleSendNotifications;
    dataForApi.customerId =
      get().appointmentData?.customers[0]?.customerId || undefined;

    const contactPersonPayload = buildContactPersonPayload({
      values,
      attendeeCustomerId: values.customerId ?? undefined,
    });
    dataForApi.customerData.contactPerson = contactPersonPayload;

    if (isNewAppointment) {
      // create new appointment

      const dataForPost = {
        appointmentParts: get().selectedServices.map((selectedService) => {
          return {
            serviceId: selectedService.serviceId,
            timeOffStart: selectedService.startDuration || undefined,
            timeOffDuration: selectedService.timeOffDuration || undefined,
            duration:
              (selectedService.startDuration || 0) +
              (selectedService.endDuration || 0) +
              (selectedService.timeOffDuration || 0),
            resources: selectedService.resources || [],
            users: selectedService.users || [],
            blockBeforeMins: selectedService.blockBeforeMins,
            blockAfterMins: selectedService.blockAfterMins,
            attendance: selectedService.attendance,
            price: selectedService.price,
            discountType: selectedService.discountType,
            discountAmount: Math.round(
              selectedService.discountAmount *
                (selectedService.discountType == "amount" ? 100 : 1),
            ),
            // Whether the customer asked for this line's staff member by name.
            // Nothing else on a desk booking can tell you — the phone call is
            // invisible to every other signal — and it is what separates
            // loyalty to a person from loyalty to the salon.
            customerRequestedStaff:
              selectedService.customerRequestedStaff ?? false,
          };
        }),
        notificationSettings: get().getNotificationSettingsForApi(),
        locationId: get().appointmentData.locationId,
        language: values.createPaymentLocale || "sl",
        notifications: values.sendNotifications,
        repeat: values.isRepeating
          ? {
              interval: values.repeatInterval,
              type: values.repeatType,
              count: values.repeatDuration,
            }
          : undefined,
        startTime: dayObject.format("YYYY-MM-DDTHH:mm"),
        customer: {
          name: values.name,
          lastName: values.lastName,
          countryCode: values.countryCode,
          email: values.email,
          gsm: values.gsm,
          customerId: values.customerId || undefined,
          comment: values.comment,
          tagIds: values.selectedTagIds,
          defaultTagIds: values.selectedDefaultIds,
          customerSubscriptionId: values.customerSubscriptionId || undefined,
          language: values.language,
        },
        contactPerson: contactPersonPayload || undefined,
        discountCodeId: values.discountCode ?? undefined,
      };

      const paymentDetails = buildPaymentDetailsForSubmit(
        values.createPayment,
        {
          paymentChannel: values.createPaymentChannel,
          onlyFirstAppointment: values.payOnlyFirstAppointment,
          paymentTimeoutMinutes: values.createPaymentTimeoutMinutes,
          overridePaymentAmount: get().isCustomOnlinePaymentAmount
            ? values.overrideCreatePaymentAmount
            : null,
        },
      );

      try {
        const response = await api.appointments.usePostAppointment({
          ...dataForPost,
          paymentDetails,
          throwIfRestricted: true,
        });

        set({
          isCreatingAppointment: false,
          lastCreatedAppointmentId:
            response?.created?.[0]?.appointmentId ?? null,
        });

        addToast({
          title: t`Uspešno ste ustvarili termin`,
          color: "success",
        });
        useCalendarStore.getState().closeModals();
      } catch (e) {
        if (e instanceof AxiosError) {
          if (e.response?.data?.canOverride) {
            const items = e.response.data.warnings;

            get().setCanDismissDrawer(false);

            get().setInvalidAppointmentsData({
              items,
              dataForPost,
            });
          } else {
            addToast({
              title: t`Napaka pri ustvarjanju termina`,
              description: e.response?.data?.error,
              color: "danger",
            });
          }
        } else {
          addToast({
            title: t`Napaka pri ustvarjanju termina`,
            color: "danger",
          });
        }
      }
    } else {
      try {
        await apiUpdateAppointment(get().appointmentId, {
          ...dataForApi,
        });

        addToast({
          title: <Trans>Uspešno ste posodobili termin</Trans>,
          color: "success",
        });
        useCalendarStore.getState().closeModals();
      } catch (e) {
        addToast({
          title: t`Napaka pri posodabljanju`,
          description: e.response?.data?.error || "",
          color: "danger",
        });
      }
    }

    set({ isCreatingAppointment: false });
  },

  fetchAppointmentData: async (apptId) => {
    if (!apptId && !get().appointmentId) return;

    set({ isFetchingData: true });
    try {
      const res = await apiGetAppointment(apptId ?? get().appointmentId);
      if (res.status !== 200) {
        throw new Error("Failed to fetch appointment data");
      }
      const fetchedAppointment = res.data.appointment;
      const selectedServices = fetchedAppointment.appointmentParts.map(
        (appPart) => {
          const discountType =
            appPart.customers?.at(0)?.discountType || "amount";
          return {
            serviceId: appPart.serviceId,
            users: appPart.users || [],
            startDuration: appPart.timeOffDuration
              ? appPart.baseTimeOffStart
              : appPart.baseDuration,
            timeOffDuration: appPart.timeOffDuration || null,
            endDuration: appPart.timeOffDuration
              ? appPart.baseDuration -
                appPart.timeOffDuration -
                appPart.baseTimeOffStart
              : null,
            price: appPart.customers?.at(0)?.price,
            resources: appPart.resources || [],
            appointmentId: appPart.appointmentId,
            blockBeforeMins: appPart.blockBeforeMins,
            blockAfterMins: appPart.blockAfterMins,
            attendance: appPart.attendance,
            discountType,
            discountAmount:
              appPart.customers?.at(0)?.discountAmount /
              (discountType === "amount" ? 100 : 1),
            invoiceId: appPart.customers?.at(0)?.invoiceId,
            customerRequestedStaff:
              getRecordedRequestedUserIds(appPart.customers).length > 0,
            requestedUserIds: getRecordedRequestedUserIds(appPart.customers),
            isStaffRequestedOnline: isRecordedRequestOnline(appPart.customers),
          };
        },
      );

      set({
        initialAppointmentData: res.data.appointment,
        appointmentData: {
          ...res.data.appointment,
          customers: fetchedAppointment.appointmentParts.flatMap(
            (appPart) => appPart.customers,
          ),
        },
        selectedServices: selectedServices,
        isFetchingData: false,
      });

      // Pre-select the customer for non-pre-scheduled appointments
      if (!res.data.appointment.isPreScheduled) {
        set({
          selectedUserAppointment:
            fetchedAppointment.appointmentParts[0].customers[0]
              .userAppointmentId,
        });
      }
    } catch (error) {
      set({ isFetchingData: false });
    }
  },

  createBreak: async (data) => {
    const calendarStoreState = useCalendarStore.getState();
    const { calendarData, newBreakData } = calendarStoreState;
    const { allShifts, breakTypes } = calendarData;

    const breakType = breakTypes.find(
      (breakType) =>
        breakType.scheduleShiftBreakTypeId ==
        newBreakData?.scheduleShiftBreakTypeId,
    );

    if (!breakType) {
      return;
    }

    const response = await apiCreateBreak(data).catch((err) => {
      addToast({
        title: <Trans>Napaka pri dodajanju pavze</Trans>,
        description: err?.response?.data?.error,
        color: "danger",
      });
    });

    if (response == null || response.status !== 200) {
      return false;
    }

    addToast({
      title: <Trans>Pavza dodana</Trans>,
      description: <Trans>Pavza je bila uspešno dodana</Trans>,
      color: "success",
    });

    return true;
  },
}));
